import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import {
  Checkbox,
  message,
  Modal,
  Spin,
} from 'antd';
import ModalWrapper from './ModalWrapper';
import AssignRecordFields from './AssignRecordFields';
import { fetchAssigneeOptions } from './assigneeSearch';
import './assignRecord.scss';
import { ARCHIVE_AFTER_RECORD_ACTIONS_FEATURE, hasFeatureInLocalStorage, assignModalTitle, assignmentStaysOpenNote, fetchArchiveClosesAssignments } from '../../global/featureFlags';
import { captureAnalytics } from '../../actions/preload';
import { archiveFailed } from '../../util/archiveOutcome';

const { confirm } = Modal;

export class AssignRecord extends Component {
  constructor(props) {
    super(props);
    this.state = {
      selectedDocumentStatus: null,
      sendToken: props.modalType.data.record,
      parentType: props.modalType.data.receiverType,
      markComplete: props.modalType.data.markComplete || false,
      assignedDocumentId: props.modalType.data.assignedDocumentId || null,
      documentStatuses: [],
      note: '',
      // Tri-state: null until the setting answers. NOT false — the note is a
      // categorical claim about archive behavior, and starting it at false made the
      // modal assert "archiving does not close it" on the first paint to
      // organizations where archiving does close it, then indefinitely whenever the
      // settings request failed. Absent-endpoint still resolves to false and still
      // shows the note; see `fetchArchiveClosesAssignments`.
      archiveClosesAssignments: null,
      archiveAfterAssign: false,
    };
    this.mounted = false;
  }

  componentDidMount = async () => {
    const { getDocumentStatuses } = this.props;
    this.mounted = true;

    // Not awaited with the statuses: the note is the only thing it decides, and
    // holding the whole modal on it would delay the fields for a line of prose.
    fetchArchiveClosesAssignments().then((archiveClosesAssignments) => {
      if (this.mounted) this.setState({ archiveClosesAssignments });
    });

    await getDocumentStatuses();
    if (!this.mounted) return;
    this.setState({ documentStatuses: this.props.allDocumentStatuses.document_statuses });
  }

  componentWillUnmount() {
    this.mounted = false;
  }

  fetchUserList = (value) => {
    const { searchAssignableUsers } = this.props;
    return fetchAssigneeOptions({ value, searchAssignableUsers });
  }

  handleMarkCompleted = async () => {
    const { markDocumentAsCompleted } = this.props;
    const {
      assignedDocumentId,
      markComplete,
    } = this.state;

    if (markComplete) {
      await markDocumentAsCompleted(assignedDocumentId);

      if (window.location.pathname.includes('/app/view/')) {
        message.success({
          content: 'Document Assigned and Marked Complete Successfully. You\'ll be redirected to the assigned records page.',
        });

        // After 3 seconds, go to assigned records page.
        setTimeout(() => { this.context.router.history.push('/app/assigned'); }, 3000);
      } else {
        message.success({
          content: 'Document Assigned and Marked Complete Successfully',
        });

        window.location.reload();
      }
    } else {
      message.success({
        content: 'Document Assigned Successfully',
      });
    }
  }

  finishAssignment = async () => {
    const { hideModal, archiveReceivedRecords, refreshCurrentRecords } = this.props;
    const { archiveAfterAssign, markComplete, sendToken } = this.state;
    hideModal();
    if (archiveAfterAssign && !markComplete) {
      // The setting can change while the dialog is open. Recheck before the
      // archive endpoint, which otherwise closes the assignment we just made.
      const closesAssignments = await fetchArchiveClosesAssignments();
      if (closesAssignments !== false) {
        message.warning({ content: 'Document assigned, but not archived because archiving may close assignments.' });
        return;
      }
      const result = await archiveReceivedRecords([sendToken], true);
      if (archiveFailed(result)) {
        message.error({ content: `Document assigned, but not archived. ${result?.response?.data?.error || 'Please try again.'}` });
        return;
      }
      message.success({ content: 'Document assigned and archived successfully' });
      refreshCurrentRecords();
      return;
    }
    this.handleMarkCompleted();
  }

  confirmAssign = async (assignedDocument) => {
    const { assignDocument } = this.props;
    const result = await assignDocument(assignedDocument);

    const { hideModal } = this.props;
    const errorMessageContent = 'Error assigning document. Please try again.';
    if (!result?.error) {
      await this.finishAssignment();
    } else {
      hideModal();
      message.error({
        content: errorMessageContent,
      });
    }
  }

  assignDocument = async () => {
    const { assignDocument } = this.props;
    const {
      sendToken,
      assignee,
      note,
      selectedDocumentStatus,
      parentType,
    } = this.state;
    const types = {
      received_fax: 'ReceivedFax', receiver: 'Receiver', email_attachment: 'EmailAttachment', reply: 'Reply',
    };
    const parent = Object.prototype.hasOwnProperty.call(types, parentType) ? types[parentType] : null;
    if (!parent || (parent === 'Reply' && !this.props.modalType.data.fileToken)) {
      message.error({ content: 'This document cannot be assigned.' });
      return;
    }

    let assignedDocument = {
      send_token: sendToken,
      note,
      assignee_id: assignee.value,
      parent_type: parent,
      record_id: this.props.modalType.data.fileToken,
      document_status_id: selectedDocumentStatus,
      force_assign: false,
    };
    const result = await assignDocument(assignedDocument);

    const { hideModal } = this.props;
    const errorMessageContent = 'Error assigning document. Please try again.';
    if (!result?.error) {
      if (result?.data?.is_department_user === false) {
        confirm({
          title: 'This person doesn\'t have access!',
          okText: 'Yes',
          cancelText: 'No',
          content: 'This user doesn\'t currently have access to the document. Would you like to grant them access to complete this assignment?',
          onOk: () => {
            assignedDocument.force_assign = true;
            return this.confirmAssign(assignedDocument);
          },
          onCancel: () => {
            return;
          },
        });
      } else {
        await this.finishAssignment();
      }
    } else {
      hideModal();
      message.error({
        content: errorMessageContent,
      });
    }
  }

  renderModal = () => {
    const { isLoading } = this.props;
    const {
      documentStatuses,
      selectedDocumentStatus,
      assignee,
      note,
      markComplete,
      archiveClosesAssignments,
      archiveAfterAssign,
    } = this.state;

    const handleAssignToChange = (e) => {
      // Cleared via allowClear: `e` is undefined. Otherwise `e` is the picked option
      // itself ({ value: email, label: name }), so it is right even when the picker's
      // responses arrived out of order.
      this.setState({ assignee: e ? { value: e.value, label: e.label } : undefined });
    };

    const handleChange = (value) => {
      this.setState({ selectedDocumentStatus: value });
    };

    if (isLoading) {
      return (
        <div className="assign-record-modal__loading">
          <Spin size="small" />
          <span>Assigning record…</span>
        </div>
      );
    }

    // Both halves of the sentence have to actually be true here.
    //
    // Mark Complete and Reassign (the assigned-records row action, the record
    // viewer's overflow menu and MarkCompleteDropdown all open this same modal with
    // `markComplete`) closes the current assignment on submit — set by the clicker,
    // not the assignee — creates a new one for the new assignee, and drops the record
    // out of /app/assigned. "The record stays in this inbox. The assignee closes the
    // assignment themselves" is false on both halves for the assignment on screen, so
    // that flow gets no note at all.
    //
    // And with archiving closing assignments, the last clause is false too.
    //
    // Only an answered `false` earns the note. `true` makes it wrong, and `null` —
    // the request still in flight, or failed — means we have no answer to base it on,
    // so the modal says nothing rather than guessing. `markComplete` suppresses it
    // either way, whatever the setting turns out to be.
    const archivingLeavesAssignmentsOpen = archiveClosesAssignments === false;
    const staysOpenNote = (!markComplete && archivingLeavesAssignmentsOpen && !archiveAfterAssign)
      ? assignmentStaysOpenNote()
      : null;

    return (
      <AssignRecordFields
        formId="notifyUser"
        onFinish={this.assignDocument}
        staysOpenNote={staysOpenNote}
        documentStatuses={documentStatuses}
        selectedDocumentStatus={selectedDocumentStatus}
        onDocumentStatusChange={handleChange}
        assignee={assignee}
        fetchUserList={this.fetchUserList}
        onAssigneeChange={handleAssignToChange}
        note={note}
        onNoteChange={(value) => this.setState({ note: value })}
      />
    );
  }

  render() {
    const { isLoading, hideModal } = this.props;
    const {
      selectedDocumentStatus, assignee, markComplete, archiveClosesAssignments, archiveAfterAssign,
    } = this.state;
    return (
      <ModalWrapper
        hideModal={hideModal}
        customContentStyle={{}}
        width="560px"
        action={this.assignDocument}
        actionName="Submit"
        dismiss={isLoading ? '' : 'Cancel'}
        disabled={isLoading || !selectedDocumentStatus || !assignee}
        modalTitle={assignModalTitle({ markComplete })}
        form="assignDocument"
        wrapClassName="assign-record-modal"
        footerLeading={hasFeatureInLocalStorage(ARCHIVE_AFTER_RECORD_ACTIONS_FEATURE) && !markComplete && archiveClosesAssignments === false && !['reply', 'Reply'].includes(this.state.parentType) && (
          <Checkbox
            checked={archiveAfterAssign}
            onChange={(event) => {
              this.setState({ archiveAfterAssign: event.target.checked });
              captureAnalytics('assign_archive_after_toggled', { enabled: event.target.checked });
            }}
          >
            Archive after assigning
          </Checkbox>
        )}
      >
        {this.renderModal()}
      </ModalWrapper>
    );
  }
}

AssignRecord.contextTypes = {
  router: PropTypes.object.isRequired,
};

AssignRecord.defaultProps = {
  error: false,
  errorCode: null,
};

AssignRecord.propTypes = {
  hideModal: PropTypes.func.isRequired,
  modalType: PropTypes.object.isRequired,
  isLoading: PropTypes.bool.isRequired,
  assignDocument: PropTypes.func.isRequired,
  markDocumentAsCompleted: PropTypes.func.isRequired,
  archiveReceivedRecords: PropTypes.func.isRequired,
  refreshCurrentRecords: PropTypes.func.isRequired,
  paramTools: PropTypes.object,
  allDocumentStatuses: PropTypes.object.isRequired,
  getDocumentStatuses: PropTypes.func.isRequired,
  searchAssignableUsers: PropTypes.func.isRequired,
  error: PropTypes.bool,
  isDepartmentUser: PropTypes.bool,
};

export default connect(state => ({
  isLoading: state.records.isLoading,
  error: state.records.error,
  errorCode: state.records.errorCode,
  allDocumentStatuses: state.userProfile.allDocumentStatuses,
  isDepartmentUser: state.records.isDepartmentUser,
}))(AssignRecord);
